import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Autocomplete, InputBase } from "@mui/material";
import { Chip, FormField, Select } from "@outpave/ds";
import { Inline, Stack } from "components/layout/reusableLayouts";
import {
  isValidIpOrCidr,
  canonicalizeIpOrCidr,
  buildIpSuggestions,
} from "utils/apiKeys/ipAllowlist";

export type AllowListMode = "ALL" | "RESTRICT";

interface IpAllowlistFieldProps {
  mode: AllowListMode;
  onModeChange: (mode: AllowListMode) => void;
  entries: string[];
  onEntriesChange: (entries: string[]) => void;
  // The draft stays lifted because both drawers refuse to submit while an
  // unconfirmed entry sits in the box: a reader who typed an IP and did not
  // press Enter believes it is on the list.
  draft: string;
  onDraftChange: (draft: string) => void;
  disabled?: boolean;
}

/**
 * The allowlist mode and its entries, for the create and edit drawers.
 *
 * The two drawers carried character-identical copies of this, its three
 * helpers and a hand-rolled label, so every fix here used to need applying
 * twice.
 *
 * The entry box is a themed MuiInputBase rather than the DS TextField, which
 * sends its ref to the input element: Autocomplete anchors its popper on the
 * ref it hands to the root, so a DS field would anchor the suggestions to the
 * wrong box.
 */
const IpAllowlistField: React.FC<IpAllowlistFieldProps> = ({
  mode,
  onModeChange,
  entries,
  onEntriesChange,
  draft,
  onDraftChange,
  disabled,
}) => {
  const { t } = useTranslation();
  const [error, setError] = useState<string | null>(null);

  const suggestions = useMemo(
    () => buildIpSuggestions(draft, entries),
    [draft, entries],
  );

  // The error belongs to the draft that produced it. A failed entry leaves the
  // draft in place, so an empty one means the parent reset the form and the
  // message is about text that is no longer there. The DS Drawer unmounts this
  // component on close today, which hides the case; that is the drawer's
  // behaviour rather than this component's contract.
  useEffect(() => {
    if (!draft) setError(null);
  }, [draft]);

  const addEntry = (raw: string) => {
    const trimmed = raw.trim();
    if (!trimmed) return;
    if (!isValidIpOrCidr(trimmed)) {
      setError(t("ApiKeyInvalidIp"));
      return;
    }
    const candidate = canonicalizeIpOrCidr(trimmed);
    if (entries.includes(candidate)) {
      setError(t("ApiKeyIpAlreadyInList"));
      return;
    }
    onEntriesChange([...entries, candidate]);
    onDraftChange("");
    setError(null);
  };

  const handleModeChange = (next: AllowListMode) => {
    onModeChange(next);
    if (next === "ALL") {
      onDraftChange("");
      setError(null);
    }
  };

  return (
    <FormField
      asFieldset
      fullWidth
      label={t("IP allowlist")}
      helperText={t("ApiKeyAllowlistHelper")}
      disabled={disabled}
    >
      <Stack gap="var(--space-4)" sx={{ marginTop: "var(--space-3)" }}>
        {/* TODO: margin top ^ should be moved to DS repo (FormField) */}
        <Select
          fullWidth
          // The fieldset legend names the group, not this picker.
          aria-label={t("IP allowlist")}
          // The mode is always one of the two; a clear x would offer a state
          // the allowlist has no meaning for.
          clearable={false}
          value={mode}
          onChange={(value) =>
            handleModeChange((value as AllowListMode) ?? mode)
          }
          disabled={disabled}
          options={[
            { id: "ALL", label: t("Allow All") },
            { id: "RESTRICT", label: t("Restrict to specific IPs") },
          ]}
        />

        {mode === "RESTRICT" && (
          <>
            <Autocomplete
              freeSolo
              disableClearable
              options={suggestions}
              filterOptions={(opts) => opts}
              inputValue={draft}
              onInputChange={(_event, value, reason) => {
                if (reason === "input" || reason === "clear") {
                  onDraftChange(value);
                  if (error) setError(null);
                }
              }}
              onChange={(_event, value) => {
                if (typeof value === "string" && value) addEntry(value);
              }}
              disabled={disabled}
              renderInput={(params) => (
                <FormField
                  fullWidth
                  // htmlFor is what associates the error with the input. The
                  // outer fieldset's legend names the group, not this control.
                  htmlFor={params.id}
                  errorText={error || undefined}
                >
                  <InputBase
                    {...params.slotProps.input}
                    id={params.id}
                    data-size="lg"
                    fullWidth
                    disabled={disabled}
                    error={!!error}
                    placeholder={t("ApiKeyAddIpPlaceholder")}
                    inputProps={{
                      ...params.slotProps.htmlInput,
                      // The placeholder is the only visible name and it goes
                      // away as soon as the reader types.
                      "aria-label": t("ApiKeyAddIpPlaceholder"),
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && suggestions.length === 0) {
                        e.preventDefault();
                        addEntry(draft);
                      }
                    }}
                  />
                </FormField>
              )}
            />

            {entries.length > 0 && (
              <Inline gap="var(--space-3)">
                {entries.map((ip) => (
                  <Chip
                    key={ip}
                    tone="neutral"
                    labels={{ remove: t("Remove") }}
                    onClear={
                      disabled
                        ? undefined
                        : () =>
                            onEntriesChange(
                              entries.filter((entry) => entry !== ip),
                            )
                    }
                  >
                    {ip}
                  </Chip>
                ))}
              </Inline>
            )}
          </>
        )}
      </Stack>
    </FormField>
  );
};

export default IpAllowlistField;
